import type { AxiosResponse } from 'axios';
import {
  BarChart3,
  Database,
  FileText,
  Home,
  Image,
  List,
  Settings,
  Shield,
  Receipt,
} from 'lucide-react';

import api from '@/utils/axiosInterceptor';
import Premission from '@/utils/premissions/premission.type.ts';

export interface IdNameI {
  ID: number;
  Name: string;
}

export const ClientCardTabs = [
  {
    label: 'عام',
    value: 'mainData',
    icon: <Home />,
    Prem: Premission.Prem_Clients_Main,
  },
  {
    label: 'احصائيات ',
    value: 'statisticsData',
    icon: <Settings />,
    Prem: Premission.Prem_Clients_Main,
  },
  {
    label: 'مشتريات ',
    value: 'purchasesData',
    icon: <BarChart3 />,
    Prem: Premission.Prem_Clients_Purchases,
  },
  {
    label: 'صور',
    value: 'clientPapers',
    icon: <Image />,
    Prem: Premission.Prem_Clients_Client_Store_Open,
  },
  {
    label: 'مستندات',
    value: 'clientDocuments',
    icon: <FileText />,
    Prem: Premission.Prem_Clients_Client_Store_Open,
  },
  {
    label: 'الموقع',
    value: 'clientLocation',
    icon: <List />,
    Prem: Premission.Prem_Clients_Location_Open,
  },
  {
    label: 'رخصة الصيدلة',
    value: 'pharmacistLicense',
    icon: <Shield />,
    Prem: Premission.Prem_Clients_License_Review,
  },
  {
    label: 'كشف الحساب',
    value: 'balanceStatement',
    icon: <Receipt />,
    Prem: Premission.Prem_Clients_Balances_List,
  },
  {
    label: 'تعديلات ',
    value: 'clientEdit',
    icon: <Database />,
    Prem: Premission.Prem_Clients_Main_Edit,
  },
];
export interface IClientData {
  ID: number;
  ClientName: string;
  CityID: number;
  ClassID: number;
  RegionID: number;
  Enabled: boolean;
  trustLimit: number;
  HasDeal: boolean;
  NormalDiscount: number;
  ClientSupplier: number;
  IsBlackListSales: boolean;
  StateID: number;
  TypeID: number;
}

export interface IMainData {
  storeType: number;
  clients: IClientList[];
}

export interface IClientList {
  City_ID: number | null;
  Class_Id: number | null;
  DelivEmp_Id: number | null;
  Enabled: boolean;
  Fax: string;
  HasDeal: boolean | null;
  ID: number;
  Mobile: string;
  Name: string;
  NormalDiscount: number;
  Region_Id: number | null;
  Tel: string;
  Tel2: string;
  Type_ID: number;
  coordinates: number[];
  createdAt: string;
  debt: number;
  isSuppliar: boolean;
  isMajor: boolean;
  paperImgs: string[];
  purchasesMan: string | null;
  suppliarClass: string | null;
  trustLimit: number;
  updatedAt: string;
  __v: number;
  _id: string;
}

export interface ICityType {
  ID: number;
  Name: string;
  StateID: number;
}

export interface IClientParameters {
  cities: ICityType[];
  states: IdNameI[];
  classes: IdNameI[];
  regions: IdNameI[];
}

export interface IClientOneData {
  ID: number;
  Code: string;
  Name: string;
  Address: string;
  CityID: number;
  CityName: string;
  StateID: number;
  StateName: string;
  Tel: string;
  Tel2: string;
  Fax: string;
  Email: string;
  Mobile: string;
  Notes: string;
  Enabled: boolean;
  ClientSupplierID: number;
  trustLimit: number;
  RegionID: number;
  RegionName: string;
  SpecialDiscount: number;
  BulkDiscount: number;
  NormalDiscount: number;
  ClassID: number;
  ClassName: string;
  ContactTel3: string;
  IsBlackListSales: boolean;
  Zip: string;
  TypeID: number;
  TypeName: string;
  ClientSupplierName: string;
  ContactName: string;
  HasDeal: boolean;
  PaymentPeriod: number;
  PaymentPeriodType: number;
  PaymentType: string;
  Debt: number;
}

export interface DocumentFile {
  _id: string;
  filename: string;
  type: DocumentType;
  uploadDate: string;
}

export interface IClientMongoData {
  coordinates: number[];
  purchasesMan: {
    _id: string;
    empName: string;
    avatar: string;
    job: {
      _id: string;
      jobName: string;
    };
  };
  telesalesAgentId?: {
    _id: string;
    empName: string;
    avatar: string;
    job: {
      _id: string;
      jobName: string;
    };
  } | null;
  isSuppliar: boolean;
  suppliarClass: string;
  paperImgs?: string[];
  documents?: DocumentFile[];
  latitude?: number;
  longitude?: number;
  googleMapsUrl?: string;
  isMajor?: boolean;
  avatar?: string;
  ID: number;
  _id: string;
}

export interface IOneClientAllData {
  mainData: IClientOneData;
  otherData: IClientMongoData;
}

export interface IEmpsList {
  id: string;
  name: string;
  avatar: string;
}

export interface MainIDData {
  id: string;
  newID: string;
}

export async function GetAllClients() {
  try {
    const response: AxiosResponse<IMainData> =
      await api.get(`/clients/get-all/all`);
    return response?.data;
  } catch (error) {
    throw error; // Ensure the error is propagated
  }
}

export async function GetClientsParameters() {
  try {
    const response: AxiosResponse<IClientParameters> =
      await api.get(`/clients/params`);
    return response?.data;
  } catch (error) {
    throw error; // Ensure the error is propagated
  }
}

export async function GetOneClient(Id: number | null) {
  try {
    const response: AxiosResponse<IOneClientAllData> = await api.get(
      `/clients/get-one/${Id}`,
    );
    return response?.data;
  } catch (error) {
    throw error; // Ensure the error is propagated
  }
}

export async function updateSuppliarClass({
  id,
  suppliarClass,
}: {
  id: string;
  suppliarClass: string;
}) {
  try {
    const response: AxiosResponse<IOneClientAllData> = await api.patch(
      `/clients/update-s-class`,
      { id, suppliarClass },
    );
    return response?.data;
  } catch (error) {
    throw error; // Ensure the error is propagated
  }
}
export async function updateSuppliarMan({
  id,
  purchasesMan,
}: {
  id: string;
  purchasesMan: string;
}) {
  try {
    const response: AxiosResponse<IOneClientAllData> = await api.patch(
      `/clients/update-s-man`,
      { id, purchasesMan },
    );
    return response?.data;
  } catch (error) {
    throw error; // Ensure the error is propagated
  }
}

export async function EmpListApi() {
  try {
    const response: AxiosResponse<IEmpsList[]> =
      await api.get(`/employee/list`);
    return response?.data;
  } catch (error) {
  }
}

export async function UpdateClientsPaperApi({
  id,
  data,
}: {
  id: string;
  data: FormData;
}) {
  const response = await api.patch(`/client-upload/papers/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response?.data;
}

export async function DeleteClientPaperApi(paperData: {
  id: string;
  papers: string[];
}) {
  const response = await api.patch(`/client-upload/papers-pull/`, paperData);
  return response?.data;
}

export async function ChangeMainIDAPI(data: MainIDData) {
  const response = await api.patch(`/clients/mainid`, data);
  return response?.data;
}

export async function UpdateClientsCacheApi() {
  try {
    const response: AxiosResponse = await api.get('/clients/reset-cache');
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export interface LocationUpdateData {
  clientId: number;
  coordinates?: number[];
  googleMapsUrl?: string;
}

export async function updateLocation(data: LocationUpdateData) {
  try {
    const response: AxiosResponse<{ success: boolean; message: string }> =
      await api.patch(`/clients/update-location`, data);
    return response?.data;
  } catch (error) {
    throw error; // Ensure the error is propagated
  }
}

export interface UpdateIsMajorData {
  id: string; // MongoDB ObjectId string
  isMajor: boolean;
}

export async function updateClientIsMajor(data: UpdateIsMajorData) {
  try {
    const response = await api.patch('/clients/update-is-major', data);
    return response?.data;
  } catch (error) {
    throw error;
  }
}

export enum DocumentType {
  COMMERCIAL_REGISTER = 'commercial_register',
  TAX_CARD = 'tax_card',
  PHARMACIST_LICENSE = 'pharmacist_license',
  CONTRACT = 'contract',
  DEAL = 'deal',
  OTHER = 'other',
}

export interface DocumentUploadData {
  id: string;
  documentType: DocumentType;
  file: File;
}

export async function uploadClientDocument(data: DocumentUploadData) {
  try {
    const formData = new FormData();
    formData.append('id', data.id);
    formData.append('documentType', data.documentType);
    formData.append('file', data.file);

    const response = await api.post('/client-upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response?.data;
  } catch (error) {
    throw error;
  }
}

export interface DocumentRemoveData {
  id: string;
  filename: string;
}

export async function removeClientDocument(data: DocumentRemoveData) {
  try {
    const response = await api.patch('/client-upload/document/remove', data);
    return response?.data;
  } catch (error) {
    throw error;
  }
}

export interface BatchUpdateMajorStatusData {
  clientGroups: {
    clients: string[];
    isMajor: boolean;
  }[];
}

export async function batchUpdateMajorStatus(data: BatchUpdateMajorStatusData) {
  try {
    const response = await api.post('/clients/batch-update-major', data);
    return response?.data;
  } catch (error) {
    throw error;
  }
}

export interface SyncNewClientsResponse {
  success: boolean;
  message: string;
  stats: {
    totalEasyStore: number;
    totalMongo: number;
    newFound: number;
    added: number;
  };
}

export async function syncNewClients(): Promise<
  AxiosResponse<SyncNewClientsResponse>
> {
  try {
    const response = await api.get('/clients/sync-new-clients');
    return response?.data;
  } catch (error) {
    throw error;
  }
}

// Balance Statement Interfaces
export interface IBalanceStatementTransaction {
  Emp_ID: number;
  Emp_Name: string;
  Trans_ID: number;
  TrDate: string;
  Amount: number;
  InOut: number;
  InOut_Name: string;
  Transaction_Effect: number;
  By_ID: number;
  By_Name: string;
  Client_ID: number;
  Client_Name: string;
  Type_Name: string;
  Invoice_Type: number;
  MainID: number;
  Store_ID: number;
  Stroe_Name: string;
  Box_ID: number;
  Notes: string;
  CashBox: boolean;
  Box_Name: string | null;
  LastDate: string;
  balanceBefore: number;
  balanceAfter: number;
  Emp_Data: {
    _id: string;
    empName: string;
    avatar: string | null;
  } | null;
  By_Data: {
    _id: string;
    empName: string;
    avatar: string | null;
  } | null;
}

export interface IBalanceStatementStatistics {
  totalTransactions: number;
  totalDebit: number;
  totalCredit: number;
  byType: {
    [key: string]: {
      count: number;
      totalAmount: number;
    };
  };
}

export interface IBalanceStatementData {
  clientId: number;
  currentBalance: number;
  openingBalance: number;
  periodBalance: number;
  closingBalance: number;
  transactions: IBalanceStatementTransaction[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
  statistics: IBalanceStatementStatistics;
}

export interface IBalanceStatementResponse {
  success: boolean;
  data: IBalanceStatementData;
  meta: {
    processingTimeMs: number;
    queriesExecuted: number;
    parallelExecution: boolean;
  };
}

export interface GetClientBalanceStatementParams {
  clientId: number;
  startDate?: string; // Format: dd-MM-yyyy
  endDate?: string; // Format: dd-MM-yyyy
}

export async function getClientBalanceStatement(
  params: GetClientBalanceStatementParams,
): Promise<AxiosResponse<IBalanceStatementResponse>> {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('clientId', params.clientId.toString());
    if (params.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate);
    }

    const response = await api.get(
      `/clients/balance-statement?${queryParams.toString()}`,
    );
    return response?.data;
  } catch (error) {
    throw error;
  }
}

// Update Trust Limit Interfaces
export interface UpdateTrustLimitData {
  clientId: number;
  trustLimit: number;
}

export interface UpdateTrustLimitResponse {
  success: boolean;
  message: string;
  data: {
    ID: number;
    Code: string;
    Name: string;
    trustLimit: number;
  };
}

export async function updateTrustLimit(
  data: UpdateTrustLimitData,
): Promise<AxiosResponse<UpdateTrustLimitResponse>> {
  try {
    const response = await api.patch('/clients/update-trust-limit', data);
    return response?.data;
  } catch (error) {
    throw error;
  }
}
